import { forwardRef } from "react";

import { observer } from "mobx-react-lite";

import { useNodeThemeColors } from "@/components/expression/theme";
import { SelectedContext, useIsSelected } from "@/components/expression/utils/layout";
import {
  useDimensionsClassNames,
  type Dimension,
} from "@/components/expression/utils/layout/NodeDimensions";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useNodeHover } from "@/components/expression/utils/useNodeHover";
import { twMerge } from "@/utils/twMerge";

import type { NodeModel } from "@/models/nodes/types";
import type { NodeSize } from "@corca/data";
import type { ComponentProps, ForwardedRef, PropsWithChildren } from "react";

const RADIUS_CLASSES: Record<NodeSize, string> = {
  sm: "rounded-5",
  xs: "rounded-5",
  xxs: "rounded-4",
  xxxs: "rounded-4",
};

type Props = {
  node: NodeModel;
  radius?: "squared" | "rounded";
  dimension?: Dimension;
  isHovered?: boolean;
  withOpacity?: boolean;
  disableHover?: boolean;
};

/**
 * Root node component that is responsible for:
 * - dimensions
 * - node theme (background, stroke)
 * - active states (hover, active, selected)
 */
const NodeContainer = forwardRef(function NodeContainer(
  {
    node,
    className,
    children,
    dimension = "square",
    radius = "squared",
    isHovered: hovered,
    disableHover,
    ...props
  }: PropsWithChildren<Props & ComponentProps<"div">>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { view, size, renderContext } = useNodeStyle();
  const { isHovered: nodeIsHovered, handlerProps } = useNodeHover(node, { disabled: disableHover });
  const isSelected = useIsSelected(node, renderContext);
  const theme = useNodeThemeColors(node);

  const isHovered = hovered || nodeIsHovered;
  const transparent = !theme?.background && !theme?.stroke;
  const isTextView = view === "text";
  const isInteractiveContext = renderContext === "editor" || renderContext === "chat-input";

  const dimensionsClassNames = useDimensionsClassNames(dimension);

  const radiusClassNames = twMerge(
    RADIUS_CLASSES[size],
    radius === "rounded" && "rounded-full",
    isTextView && "rounded-none",
  );

  const colorClassNames = twMerge(
    !theme?.stroke && theme?.background && "ring-transparent shadow-node-outline",
    theme?.background && `bg-${theme.background.light} dark:bg-${theme.background.dark}`,
    theme?.stroke &&
      `shadow-node-outline-border ring-${theme.stroke.light} dark:ring-${theme.stroke.dark}`,

    isTextView && `shadow-none bg-transparent dark:bg-transparent ring-0`,
  );

  const addHoverStyles = isInteractiveContext && !isTextView && isHovered;

  const activeClassNames = twMerge(
    "print:shadow-none print:ring-0",

    isInteractiveContext &&
      isSelected &&
      "bg-selected-bg text-selected-fg dark:bg-selected-bg dark:text-selected-fg ring-transparent",

    addHoverStyles && !transparent && "shadow-node-outline-border-hover",

    addHoverStyles && transparent && "bg-node-hover-bg ring-3 ring-node-hover-bg",
  );

  return (
    <div
      {...props}
      {...handlerProps}
      // prevent screen readers from reading the node container in editing mode
      role="none"
      className={twMerge(
        "relative flex flex-row justify-center items-center print-color-exact",
        dimensionsClassNames,
        radiusClassNames,
        colorClassNames,
        activeClassNames,
        className,
      )}
      ref={ref}
    >
      <SelectedContext.Provider value={isSelected}>{children}</SelectedContext.Provider>
    </div>
  );
});

NodeContainer.displayName = "NodeContainer";

const ObservableNodeContainer = observer(NodeContainer);

export { ObservableNodeContainer as NodeContainer };
